import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";

export default function Profile() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<{
    email: string;
    name: string | null;
    age: string | null;
    company_name: string | null;
    phone: string | null;
  } | null>(null);

  useEffect(() => {
    getProfile();
  }, []);

  async function getProfile() {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        throw new Error('No session found');
      }

      let { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', session.user.id)
        .single();

      if (error) {
        throw error;
      }

      setProfile({
        email: data.email,
        name: data.name,
        age: data.age,
        company_name: data.company_name,
        phone: data.phone
      });
    } catch (error) {
      console.error('Error loading profile:', error);
      toast({
        title: "Error",
        description: "Error loading profile",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }

  async function updateProfile() {
    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        throw new Error('No session found');
      }

      if (!profile) {
        throw new Error('No profile data');
      }

      const updates = {
        id: session.user.id,
        email: profile.email, // Include email as it's required
        name: profile.name,
        age: profile.age,
        company_name: profile.company_name,
        phone: profile.phone,
        updated_at: new Date().toISOString(),
      };

      let { error } = await supabase
        .from('profiles')
        .upsert(updates);

      if (error) {
        throw error;
      }

      toast({
        title: "Success",
        description: "Profile updated successfully",
      });
    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        title: "Error",
        description: "Error updating profile",
        variant: "destructive",
      });
    }
  }

  if (loading) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="p-6">
      <Card className="max-w-2xl mx-auto p-6">
        <h1 className="text-2xl font-bold mb-6">Profile</h1>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Email</label>
            <Input
              type="email"
              value={profile?.email || ''}
              disabled
              className="bg-gray-100"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Name</label>
            <Input
              type="text"
              value={profile?.name || ''}
              onChange={(e) => setProfile(prev => ({ ...prev!, name: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Age</label>
            <Input
              type="text"
              value={profile?.age || ''}
              onChange={(e) => setProfile(prev => ({ ...prev!, age: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Company Name</label>
            <Input
              type="text"
              value={profile?.company_name || ''}
              onChange={(e) => setProfile(prev => ({ ...prev!, company_name: e.target.value }))}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Phone</label>
            <Input
              type="tel"
              value={profile?.phone || ''}
              onChange={(e) => setProfile(prev => ({ ...prev!, phone: e.target.value }))}
            />
          </div>
          <Button onClick={updateProfile} className="w-full">
            Update Profile
          </Button>
        </div>
      </Card>
    </div>
  );
}